import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import AppBreadcrumbs from "@/components/AppBreadcrumbs";
import { toast } from "sonner";
import {
  Brain,
  Briefcase,
  Check,
  ChatCircle,
  ClipboardText,
  ClockCounterClockwise,
  Flag,
  Fingerprint,
  Handshake,
  Lightbulb,
  ListBullets,
  MagnifyingGlass,
  PencilSimple,
  Quotes,
  Sparkle,
  SlidersHorizontal,
  Dna,
  TrendUp,
  UserCircle,
  WarningCircle,
  X,
} from "@phosphor-icons/react";

const FIELD_DEFS = [
  { key: "sentence_length", label: "Sentence length", type: "text", icon: ClipboardText },
  { key: "paragraph_structure", label: "Paragraph structure", type: "text", icon: ListBullets },
  { key: "common_transitions", label: "Common transitions", type: "array", icon: TrendUp },
  { key: "favorite_verbs", label: "Favorite verbs", type: "array", icon: Sparkle },
  { key: "banned_phrases", label: "Banned phrases", type: "array", icon: WarningCircle },
  { key: "detail_level", label: "Detail level", type: "text", icon: MagnifyingGlass },
  { key: "first_person_habits", label: "First-person habits", type: "text", icon: UserCircle },
  { key: "negotiation_dynamics_style", label: "Negotiation dynamics", type: "text", icon: Handshake },
  { key: "objection_framing_style", label: "Objection framing", type: "text", icon: Flag },
  { key: "reasoning_style", label: "Reasoning style", type: "text", icon: Brain },
  { key: "business_context_amount", label: "Business context", type: "text", icon: Briefcase },
  { key: "tone", label: "Tone", type: "text", icon: ChatCircle },
  { key: "pacing", label: "Pacing", type: "text", icon: ClockCounterClockwise },
  { key: "vocabulary", label: "Signature vocabulary", type: "array", icon: Quotes },
];

function EditableField({ def, value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const Icon = def.icon || Lightbulb;

  useEffect(() => { setDraft(value); }, [value]);

  const save = async () => {
    let out = draft;
    if (def.type === "array" && typeof draft === "string") {
      out = draft.split(/[,\n]/).map((item) => item.trim()).filter(Boolean);
    }
    await onSave(def.key, out);
    setEditing(false);
  };

  const startEditing = () => {
    setDraft(Array.isArray(value) ? value.join(", ") : (value || ""));
    setEditing(true);
  };

  const cancelEditing = () => {
    setDraft(value);
    setEditing(false);
  };

  const display = Array.isArray(value) ? value : (value || "not evident");

  return (
    <article
      className={`dna-field-card${editing ? " is-editing" : ""}`}
      data-testid={`dna-field-${def.key}`}
    >
      <div className="dna-field-card__header">
        <div className="dna-field-card__identity">
          <span className="dna-field-card__icon" aria-hidden="true">
            <Icon size={16} weight="duotone" />
          </span>
          <span>{def.label}</span>
        </div>

        {!editing ? (
          <button
            type="button"
            onClick={startEditing}
            data-testid={`edit-${def.key}`}
            className="dna-field-card__icon-button"
            aria-label={`Edit ${def.label}`}
            title={`Edit ${def.label}`}
          >
            <PencilSimple size={14} />
          </button>
        ) : (
          <div className="dna-field-card__actions">
            <button
              type="button"
              onClick={save}
              data-testid={`save-${def.key}`}
              className="dna-field-card__icon-button is-save"
              aria-label={`Save ${def.label}`}
              title="Save"
            >
              <Check size={15} weight="bold" />
            </button>
            <button
              type="button"
              onClick={cancelEditing}
              className="dna-field-card__icon-button"
              aria-label={`Cancel editing ${def.label}`}
              title="Cancel"
            >
              <X size={15} weight="bold" />
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <textarea
          value={draft ?? ""}
          onChange={(event) => setDraft(event.target.value)}
          className="dna-field-card__textarea"
          placeholder={def.type === "array" ? "Comma or newline separated" : "Describe this writing trait"}
          autoFocus
        />
      ) : def.type === "array" && Array.isArray(display) && display.length > 0 ? (
        <div className="dna-field-card__tags">
          {display.map((item, index) => (
            <span key={`${item}-${index}`}>{item}</span>
          ))}
        </div>
      ) : (
        <p className={`dna-field-card__value${display === "not evident" ? " is-empty" : ""}`}>
          {Array.isArray(display) ? "not evident" : display}
        </p>
      )}
    </article>
  );
}

export default function ProfilePage() {
  const [samples, setSamples] = useState("");
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("summary");

  const load = async () => {
    const { data } = await api.get("/writing-profile");
    if (data && Object.keys(data).length) {
      setProfile(data);
      if (data.raw_samples) setSamples(data.raw_samples);
      setTab("summary");
    } else {
      setTab("train");
    }
  };

  useEffect(() => { load(); }, []);

  const train = async () => {
    if (!samples.trim()) return toast.error("Paste your CRM notes first");
    setSaving(true);
    try {
      const { data } = await api.post("/writing-profile", { samples });
      setProfile(data.profile);
      toast.success("Writing DNA extracted");
      setTab("summary");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to extract DNA");
    } finally {
      setSaving(false);
    }
  };

  const patch = async (field, value) => {
    try {
      const { data } = await api.patch("/writing-profile", { field, value });
      setProfile(data.profile);
      toast.success("Updated");
    } catch {
      toast.error("Save failed");
    }
  };

  const noteCount = profile?.sample_count || 0;
  const profileConfidence = profile?.profile_confidence;
  const profileMaturity = profile?.profile_maturity_label || "Not calibrated";
  const traitCoverage = profile?.trait_coverage || 0;
  const detectedNotes = samples.split(/\n\s*\n/).filter((sample) => sample.trim()).length;

  return (
    <div className="writing-dna-page">
      <Nav />

      <main className="writing-dna-shell">
        <AppBreadcrumbs items={[{ label: "Writing DNA" }]} />
        <section className="writing-dna-hero">
          <div className="writing-dna-hero__copy">
            <div className="brand-eyebrow">Writing DNA</div>
            <h1>Your voice, decoded</h1>
            <p>
              Teach Callsect how you actually write. It extracts fourteen repeatable traits from real CRM notes,
              then uses them to make every future note sound recognizably yours.
            </p>

            <div className="writing-dna-hero__summary" aria-label="Writing DNA summary">
              <span>
                <ClipboardText size={15} weight="duotone" />
                {profile ? `${noteCount || 0} notes analyzed` : "Ready to train"}
              </span>
              <span>
                <SlidersHorizontal size={15} weight="duotone" />
                14 writing dimensions
              </span>
              <span>
                <PencilSimple size={15} weight="duotone" />
                Fully editable
              </span>
              <span>
                <Dna size={15} weight="duotone" />
                {profile ? `${profileMaturity}${Number.isFinite(profileConfidence) ? ` · ${profileConfidence}% confidence` : ""}` : "Not calibrated"}
              </span>
            </div>
          </div>
        </section>

        <nav className="writing-dna-tabs" aria-label="Writing DNA views">
          {[
            { key: "summary", label: "DNA Summary", disabled: !profile },
            { key: "train", label: profile ? "Train / Re-train" : "Train DNA" },
          ].map((item) => (
            <button
              type="button"
              key={item.key}
              disabled={item.disabled}
              data-testid={`tab-${item.key}`}
              onClick={() => setTab(item.key)}
              className={tab === item.key ? "is-active" : ""}
              aria-current={tab === item.key ? "page" : undefined}
            >
              {item.key === "summary" ? <Fingerprint size={16} weight="duotone" /> : <Dna size={16} weight="duotone" />}
              {item.label}
            </button>
          ))}
        </nav>

        {tab === "train" && (
          <section className="writing-dna-train">
            <div className="writing-dna-panel-header">
              <div>
                <div className="writing-dna-section-label">Training material</div>
                <h2>{profile ? "Refine your Writing DNA" : "Build your Writing DNA"}</h2>
                <p>Paste a coherent set of real CRM notes and separate each one with a blank line. More representative examples improve profile confidence; they do not manufacture a higher match score.</p>
              </div>
              {profile && (
                <span className="writing-dna-overwrite-note">
                  <WarningCircle size={14} weight="duotone" />
                  Retraining replaces the current profile
                </span>
              )}
            </div>

            <div className="segmented-divider" />

            <textarea
              data-testid="samples-textarea"
              value={samples}
              onChange={(event) => setSamples(event.target.value)}
              placeholder={"Paste past CRM notes here.\n\nSeparate each note with a blank line.\n\nThe more real examples you provide, the sharper the Writing DNA becomes."}
              className="writing-dna-samples"
            />

            <div className="writing-dna-train__footer">
              <div className="writing-dna-sample-stats">
                <span><ClipboardText size={14} /> {samples.length.toLocaleString()} characters</span>
                <span><ListBullets size={14} /> ~{detectedNotes} notes detected</span>
              </div>

              <button
                type="button"
                data-testid="train-dna-btn"
                onClick={train}
                disabled={saving}
                className="writing-dna-primary-button"
              >
                <Dna size={15} weight="duotone" />
                {saving ? "Extracting DNA..." : profile ? "Re-train DNA" : "Extract Writing DNA"}
              </button>
            </div>
          </section>
        )}

        {tab === "summary" && profile && (
          <section className="writing-dna-summary">
            <div className="writing-dna-stats">
              <article className="writing-dna-stat-card">
                <span className="writing-dna-stat-card__icon"><ClipboardText size={18} weight="duotone" /></span>
                <div>
                  <small>Notes analyzed</small>
                  <strong>{noteCount || "—"}</strong>
                  <p>Source examples shaping the profile.</p>
                </div>
              </article>

              <article className="writing-dna-stat-card writing-dna-stat-card--confidence">
                <span className="writing-dna-stat-card__icon"><Dna size={18} weight="duotone" /></span>
                <div>
                  <small>Profile confidence</small>
                  <strong>{Number.isFinite(profileConfidence) ? `${profileConfidence}%` : "—"}</strong>
                  <p>{profileMaturity}. How reliably Callsect understands your voice.</p>
                </div>
              </article>

              <article className="writing-dna-stat-card">
                <span className="writing-dna-stat-card__icon"><Sparkle size={18} weight="duotone" /></span>
                <div>
                  <small>Trait coverage</small>
                  <strong>{traitCoverage}%</strong>
                  <p>{profile.trait_coverage_count || 0} of 14 dimensions are grounded in the samples.</p>
                </div>
              </article>

              <article className="writing-dna-stat-card writing-dna-stat-card--tone">
                <span className="writing-dna-stat-card__icon"><ChatCircle size={18} weight="duotone" /></span>
                <div>
                  <small>Detected tone</small>
                  <strong>{profile.tone || "—"}</strong>
                  <p>The dominant voice applied to future notes.</p>
                </div>
              </article>
            </div>

            <article className="writing-dna-calibration" data-testid="dna-calibration">
              <div className="writing-dna-calibration__head">
                <div>
                  <span>Profile maturity</span>
                  <strong>{profileMaturity}</strong>
                </div>
                <span>{Number.isFinite(profileConfidence) ? `${profileConfidence}%` : "Not calibrated"}</span>
              </div>
              <div className="writing-dna-calibration__track" aria-label={`Writing DNA profile confidence ${profileConfidence || 0}%`}>
                <span style={{ width: `${profileConfidence || 0}%` }} />
              </div>
              <p>{profile.profile_confidence_reason || "Add representative CRM notes to calibrate your writing profile."}</p>
              <div className="writing-dna-calibration__explain">
                <strong>Profile confidence</strong> measures how well Callsect knows your voice.
                <strong>Writing DNA Match</strong> in Note Quality measures how closely one specific generated note follows it.
              </div>
            </article>

            <div className="writing-dna-profile-header">
              <div>
                <div className="writing-dna-section-label">Learned profile</div>
                <h2>Fourteen dimensions of your writing</h2>
                <p>Edit any rule directly. Changes are saved to the profile used for future CRM notes.</p>
              </div>
            </div>

            <div className="writing-dna-fields">
              {FIELD_DEFS.map((def) => (
                <EditableField key={def.key} def={def} value={profile[def.key]} onSave={patch} />
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
